import { useCallback, useEffect, useState } from "react";
import { Download, ExternalLink, FileCheck2, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const when = (value) => (value ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const dayOf = (value) => (value ? new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const ReportsTab = ({ onOpen }) => {
  const [filters, setFilters] = useState({ q: "", client_id: "", provider_id: "", service: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [checks, setChecks] = useState({});

  const load = useCallback(async () => {
    try {
      const response = await api.get("/evaluation-pipeline/reports", { params: filters });
      setData(response.data);
      setError("");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }, [filters]);

  useEffect(() => {
    const handle = setTimeout(load, filters.q ? 250 : 0);
    return () => clearTimeout(handle);
  }, [load, filters.q]);

  const download = async (row) => {
    const response = await api.get(`/client-evaluation-requests/${row.id}/report/download`, { params: { mode: "download" }, responseType: "blob" });
    const href = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = href;
    link.download = row.report?.original_filename || "evaluation-report";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
    load();
  };

  const verify = async () => {
    setChecking(true);
    try {
      const response = await api.post("/evaluation-pipeline/reports/verify", {});
      setChecks(Object.fromEntries(response.data.results.map((r) => [r.id, r])));
      load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setChecking(false);
    }
  };

  const items = data?.items || [];
  const update = (patch) => setFilters((current) => ({ ...current, ...patch }));
  return (
    <section className="reports-tab" data-testid="pipeline-reports-tab">
      <div className="pipeline-filters reports-filters" data-testid="reports-filters">
        <label className="pipeline-search"><Search size={15} /><input data-testid="reports-search" placeholder="Search initials (e.g. C.A.), school, provider, service or file" value={filters.q} onChange={(event) => update({ q: event.target.value })} /></label>
        <select data-testid="reports-filter-client" value={filters.client_id} onChange={(event) => update({ client_id: event.target.value })}>
          <option value="">All schools</option>
          {(data?.filters?.clients || []).map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
        </select>
        <select data-testid="reports-filter-provider" value={filters.provider_id} onChange={(event) => update({ provider_id: event.target.value })}>
          <option value="">All providers</option>
          {(data?.filters?.providers || []).map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
        </select>
        <select data-testid="reports-filter-service" value={filters.service} onChange={(event) => update({ service: event.target.value })}>
          <option value="">All services</option>
          {(data?.filters?.services || []).map((service) => <option key={service} value={service}>{service}</option>)}
        </select>
        <label className="reports-date"><span>From</span><input data-testid="reports-date-from" type="date" value={filters.date_from} onChange={(event) => update({ date_from: event.target.value })} /></label>
        <label className="reports-date"><span>To</span><input data-testid="reports-date-to" type="date" value={filters.date_to} onChange={(event) => update({ date_to: event.target.value })} /></label>
        <button className="ghost-button" data-testid="reports-verify" type="button" disabled={checking} onClick={verify}><FileCheck2 size={14} /> {checking ? "Checking files…" : "Check files"}</button>
      </div>
      {error && <p className="form-message error-message" data-testid="reports-error">{error}</p>}
      <p className="muted-text" data-testid="reports-count">{data ? `${data.total} report${data.total === 1 ? "" : "s"}` : "Loading reports…"}{Object.keys(checks).length ? ` · checked ${Object.keys(checks).length}, ${Object.values(checks).filter((c) => !c.ok).length} missing` : ""}</p>
      <table className="data-table reports-table" data-testid="reports-table">
        <thead><tr><th>Student</th><th>School</th><th>Service</th><th>Provider</th><th>Report</th><th>Uploaded</th><th>Status</th><th>Released</th><th>Activity</th><th></th></tr></thead>
        <tbody>
          {items.map((row) => {
            const check = checks[row.id];
            const missing = row.report?.file_missing_at && !(check && check.ok);
            return (
              <tr key={row.id} data-testid={`reports-row-${row.id}`} className={missing ? "late" : ""}>
                <td><button className="table-link" type="button" data-testid={`reports-open-${row.id}`} onClick={() => onOpen(row.id)}><strong>{row.student_initials}</strong></button></td>
                <td>{row.client_name}</td>
                <td>{row.service}</td>
                <td>{row.provider_name || "—"}</td>
                <td>
                  {row.has_file ? <span data-testid={`reports-file-${row.id}`}>{row.report.original_filename}{row.versions ? <small className="muted-text"> · {row.versions} previous</small> : null}</span> : <em className="muted-text" data-testid={`reports-nofile-${row.id}`}>Delivered outside TAGForce — no file yet</em>}
                  {row.external && row.has_file && <small className="muted-text"> · external: {row.external.note}</small>}
                  {check && <small className={check.ok ? "status-chip status-active" : "status-chip status-late"} data-testid={`reports-check-${row.id}`} style={{ marginLeft: 6 }}>{check.ok ? "Opens" : "Missing"}</small>}
                  {!check && missing && <small className="status-chip status-late" style={{ marginLeft: 6 }}>Missing</small>}
                </td>
                <td>{row.report ? <>{when(row.report.uploaded_at)}<br /><small className="muted-text">{row.report.uploaded_by_name}</small></> : "—"}</td>
                <td>{row.stage}{row.billing_status ? <><br /><small className="muted-text">{row.billing_status}</small></> : null}</td>
                <td>{row.released_at ? dayOf(row.released_at) : "—"}</td>
                <td><small>{row.access.views} views · {row.access.downloads} downloads{row.access.last_by_name ? <><br />last {row.access.last_by_name}</> : null}</small></td>
                <td className="reports-actions">
                  {row.has_file && <a className="ghost-button" data-testid={`reports-view-${row.id}`} href={`${BACKEND}/api/client-evaluation-requests/${row.id}/report/download?mode=view`} target="_blank" rel="noreferrer" onClick={() => setTimeout(load, 800)}><ExternalLink size={13} /> View</a>}
                  {row.has_file && <button className="ghost-button" data-testid={`reports-download-${row.id}`} type="button" onClick={() => download(row)}><Download size={13} /> Download</button>}
                  {!row.has_file && <button className="ghost-button" data-testid={`reports-attach-${row.id}`} type="button" onClick={() => onOpen(row.id)}>Attach report file</button>}
                </td>
              </tr>
            );
          })}
          {data && items.length === 0 && <tr><td colSpan={10}><p className="pipeline-empty" data-testid="reports-empty">No reports match.</p></td></tr>}
        </tbody>
      </table>
    </section>
  );
};
